// src/Pages/AiTrading/AiTrading.js
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Card,
  CardContent,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  useMediaQuery,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { fetchAIBalances } from '../../store/Slices/assetSlice';
import { fetchPriceAndPrediction, resetPaymentStatus, fetchTrades } from '../../store/Slices/aiTradingSlice';
import ManualTradeForm from './ManualTradeForm';
import AutomatedTradeButton from './AutomatedTradeButton';
import TradesTable from './TradesTable';
import PriceChart from './PriceChart';
import PredictionPaymentForm from './PredictionPaymentForm';
import TopNav from '../../Components/NavComponents/TopNav';
import BottomNav from '../../Components/NavComponents/BottomNav';
import api from '../../API/api';

const TAX_TIERS = [
  { k: '≤ 100', v: '50%' },
  { k: '≤ 200', v: '35%' },
  { k: '< 500', v: '20%' },
  { k: '< 1000', v: '10%' },
  { k: '≥ 1000', v: '3%' },
];

const AiTrading = () => {
  const dispatch = useDispatch();
  const isDesktop = useMediaQuery('(min-width:1280px)');
  const { currentPrice, pricePrediction, hasPaidForPrediction, trades, loading: tradingLoading } = useSelector((state) => state.aiTrading);
  const { aiBalances, assetsLoading } = useSelector((state) => state.assets);
  const { light } = useSelector((state) => state.theme);

  const balance = aiBalances[0]?.balance ?? 0;
  const assetSymbol = aiBalances[0]?.asset_id?.symbol ?? 'USDT';

  const mounted = useRef(true);
  const [openTax, setOpenTax] = useState(false);

  useEffect(() => {
    // Initial fetch
    dispatch(fetchAIBalances());
    dispatch(fetchTrades());
    return () => {
      mounted.current = false;
    };
  }, [dispatch]);

  useEffect(() => {
    // Check payment status for predictions
    const checkPaymentStatus = async () => {
      try {
        await api.get('/ai-trades/price-prediction');
      } catch (error) {
        if (error.response?.data?.error === 'Payment required to view price prediction' && mounted.current) {
          dispatch(resetPaymentStatus());
        }
      }
    };
    checkPaymentStatus();
  }, [dispatch]);

  useEffect(() => {
    if (hasPaidForPrediction && mounted.current) {
      dispatch(fetchPriceAndPrediction());
    }
  }, [hasPaidForPrediction, dispatch]);

  const handleRefreshBalance = () => {
    dispatch(fetchAIBalances());
  };

  const surfaceBg = light ? '#fff' : '#2c2c2c';
  const textColor = light ? '#1a1a1a' : '#fff';

  return (
    <Box sx={{ bgcolor: light ? '#f5f7fa' : '#1a1a1a', minHeight: '100vh', transition: 'background-color 0.3s' }}>
      {isDesktop && <TopNav />}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography
          variant="h4"
          align="center"
          sx={{
            mb: 4,
            fontWeight: 'bold',
            color: textColor,
            animation: 'fadeIn 1s ease-in',
            '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
          }}
        >
          AI Trading Dashboard
        </Typography>

        {/* Balance Card */}
        <Card
          sx={{
            mb: 4,
            bgcolor: surfaceBg,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            transition: 'background-color 0.3s',
          }}
        >
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'medium', color: textColor }}>
                AI Wallet Balance
              </Typography>
              <Typography variant="h5" sx={{ color: '#2ecc71' }}>
                {assetsLoading ? 'Loading...' : `$${balance.toFixed(2)} ${assetSymbol}`}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="outlined"
                sx={{
                  borderColor: '#F3BA2F',
                  color: '#F3BA2F',
                  '&:hover': { bgcolor: '#F3BA2F', color: '#fff' },
                  flex: { xs: 1, sm: 'unset' },
                }}
                onClick={handleRefreshBalance}
                disabled={assetsLoading}
              >
                Refresh Balance
              </Button>

              {/* NEW: Tax info icon + popup */}
              <Tooltip title="View AI Trades Tax System" arrow>
                <span>
                  <IconButton
                    aria-label="AI Trades Tax System"
                    onClick={() => setOpenTax(true)}
                    sx={{
                      border: '1px solid',
                      borderColor: light ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.2)',
                      borderRadius: 2,
                    }}
                  >
                    <InfoOutlinedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                bgcolor: surfaceBg,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                transition: 'background-color 0.3s',
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium', color: textColor }}>
                Manual Trading
              </Typography>
              <ManualTradeForm />
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                bgcolor: surfaceBg,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                transition: 'background-color 0.3s',
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium', color: textColor }}>
                Automated Trading
              </Typography>
              <AutomatedTradeButton />
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                bgcolor: surfaceBg,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                transition: 'background-color 0.3s',
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium', color: textColor }}>
                Current & Predicted Prices
              </Typography>

              {hasPaidForPrediction ? (
                <>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        bgcolor: light ? '#ecf0f1' : '#3c3c3c',
                        p: 1,
                        borderRadius: 1,
                        color: textColor,
                      }}
                    >
                      Current Price:{' '}
                      {tradingLoading || !currentPrice || !currentPrice.price
                        ? 'Waiting for price...'
                        : `$${currentPrice.price.toFixed(2)}`}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        bgcolor: light ? '#ecf0f1' : '#3c3c3c',
                        p: 1,
                        borderRadius: 1,
                        color: textColor,
                      }}
                    >
                      Predicted Price:{' '}
                      {tradingLoading || !pricePrediction ? 'Loading...' : `$${pricePrediction.predictedPrice.toFixed(2)}`}
                    </Typography>
                  </Box>
                  <PriceChart />
                </>
              ) : (
                <PredictionPaymentForm />
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                bgcolor: surfaceBg,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                transition: 'background-color 0.3s',
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium', color: textColor }}>
                Trades
              </Typography>
              <TradesTable />
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {!isDesktop && <BottomNav />}

      {/* ===== Tax System Dialog ===== */}
      <Dialog
        open={openTax}
        onClose={() => setOpenTax(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: surfaceBg,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: textColor }}>Tax System (AI Trades)</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: textColor, opacity: 0.85, mb: 2 }}>
            The tax is applied on profit only (if profit is positive), and the rate depends on your AI wallet balance:
          </Typography>

          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: textColor }}>Wallet Balance (USDT)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: textColor }}>
                  Tax Rate
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {TAX_TIERS.map((row, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ color: textColor }}>{row.k}</TableCell>
                  <TableCell align="right" sx={{ color: textColor }}>
                    {row.v}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Divider sx={{ my: 1 }} />

          <Typography variant="body2" sx={{ color: textColor, opacity: 0.9 }}>
            <strong>Example:</strong> If your AI wallet balance is <strong>120 USDT</strong>, your tax tier is{' '}
            <strong>35%</strong>. If a trade closes with <strong>+10.00 USDT</strong> profit, the tax is{' '}
            <strong>3.50 USDT</strong> and your net profit is <strong>6.50 USDT</strong>.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="contained"
            onClick={() => setOpenTax(false)}
            sx={{ borderRadius: 2, bgcolor: '#F3BA2F', color: '#1a1a1a', '&:hover': { bgcolor: '#d9a51f' } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* ===== End Tax System Dialog ===== */}
    </Box>
  );
};

export default AiTrading;
